import { SetStateAction } from 'react';

import { Paragraph1 } from 'inpock-ui-react';

import { Box, Icon, QuantityController } from '@/components';
import { useToast } from '@/hooks';
import { Campaign } from '@/apis';

import { TempCartData, 최대구매가능수량 } from '../OptionSelectSheet';

interface CartItemProps {
  campaign: Campaign;
  carts: TempCartData[];
  setCarts: React.Dispatch<SetStateAction<TempCartData[]>>;
}

const 임시장바구니이름 = (campaign: Campaign, cart: TempCartData) => {
  const 옵션이름 = cart.option?.name || '';
  const 옵션추가가격 = cart.option?.price ? cart.option?.price - campaign.price : 0;
  const 상세옵션이름 = cart?.sub_option ? cart.sub_option.name : '';
  const 상세옵션추가가격 = cart.sub_option?.price
    ? cart.sub_option.price - cart.sub_option.price
    : 0;

  // 옵션이 없는 경우
  if (!cart.option) {
    // 추가 상품인 경우
    if (cart.additional_product) {
      return `[추가 상품] ${cart.additional_product.additional_product_name} / ${cart.additional_product.option_name}`;
    }
    return `${campaign.name}`;
  }

  const 총추가가격 = 옵션추가가격 + 상세옵션추가가격;
  return (
    옵션이름 +
    (cart?.sub_option ? ' / ' + 상세옵션이름 : '') +
    (총추가가격 !== 0 ? ` (${총추가가격 >= 0 ? '+' : ''}${총추가가격.toLocaleString()}원)` : '')
  );
};

export function CartItem(props: CartItemProps) {
  const toastOn = useToast();
  const { carts, setCarts } = props;

  const deleteCart = (cartIndex: number) => {
    setCarts([...carts.filter((_, index) => index !== cartIndex)]);
  };

  const totalPrice = (campaign: Campaign, item: TempCartData) => {
    if (!item.option) {
      if (item.additional_product) return item.additional_product.price.toLocaleString();
      return campaign.price.toLocaleString();
    }
    const total = (item.option?.price || 0) + (item.sub_option?.price || 0);
    return (item.stock_quantity * total).toLocaleString();
  };

  const handleCartQuantity = (cartIndex: number, s: number) => {
    const clickedCart = props.carts.find((_, index) => index === cartIndex);
    if (clickedCart && clickedCart.stock_quantity + s * 1 === 0) return;

    if (clickedCart && 최대구매가능수량(clickedCart) < clickedCart.stock_quantity + s * 1)
      return toastOn('최대 구매가능수량에 도달했습니다 👏🏻');

    const newCart = Object.assign({
      ...clickedCart,
      stock_quantity: clickedCart && clickedCart.stock_quantity + s * 1,
    });
    carts.splice(cartIndex, 1, newCart);
    setCarts([...props.carts]);
  };

  return (
    <Box
      sx={{
        marginTop: '20px',
        position: 'relative',
      }}
    >
      <div>
        {carts.map((cart, index) => (
          <div
            key={`${cart.campaign.id}-${cart.option?.id}-${cart.sub_option?.id}-${cart.additional_product?.id}`}
            className="mt-24"
          >
            <Box flex justifyContent="space-between" alignItems="center">
              <Box>
                <Paragraph1 style={{ fontWeight: 500, fontSize: '18px' }} className="mb-4">
                  {임시장바구니이름(props.campaign, cart)}
                </Paragraph1>
                <Paragraph1 className="text-gray-700" style={{ fontWeight: 400 }}>
                  {totalPrice(props.campaign, cart)}원
                </Paragraph1>
              </Box>
              <Box flex alignItems="center" sx={{ gap: '14px' }}>
                <Box flex alignItems="center" justifyContent="space-between">
                  <QuantityController
                    quantity={cart.stock_quantity}
                    minus={{
                      onClick: () => handleCartQuantity(index, -1),
                      disabled: cart.stock_quantity === 1,
                    }}
                    plus={{
                      onClick: () => handleCartQuantity(index, 1),
                      disabled: cart.stock_quantity === 최대구매가능수량(cart),
                    }}
                  />
                </Box>
                {(cart.option || cart.additional_product) && (
                  <Icon icon="CloseBlack" size={16} onClick={() => deleteCart(index)} />
                )}
              </Box>
            </Box>
          </div>
        ))}
      </div>
    </Box>
  );
}
